import React, { FunctionComponent, useState } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQuery, useMutation } from "@apollo/client";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Select, { components } from "react-select";
import { ExpandLess, ExpandMore, InfoOutlined } from "@mui/icons-material";
import { GET_REGISTRATION_OPTIONS, SAVE_STUDENT_PROFILE } from "../../../pages/AdminLoginPage/api/AdminLoginQuery";
import { messages, patterns, LOGOUT_MESSAGE_NOT_IN_MEETING } from "../../../utils/constants";
import ButtonStyle from "../../../components/ButtonStyle/ButtonStyle";
import ConfirmationPopup from "../../../components/ConfirmationPopup/ConfirmationPopup";
import { useAppDispatch } from "../../../redux/app/hooks";
import { logout } from "../../../redux/slices/authSlice";
import images from "../../../assets/images";
import ToasterWithMessage from "../../../components/toaster";
import "./VirtualClassOnboardingModal.scss";

export interface IVirtualClassOnboardingInput {
  gender: string;
  dob: string;
  parentName: string;
  relationship: string;
  parentPhone: string;
  parentEmail?: string;
  currentPreviousSchoolName: string;
  academicLevel: string;
  communicationGroups: string[];
  location: string;
  referralSource?: string;
  referralDetail?: string;
  registeredSimName?: string;
  networkProvider?: string;
  dataOptInFor?: string;
}

interface VirtualClassOnboardingModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onSubmitSuccess?: (data: IVirtualClassOnboardingInput) => void;
  isLoading?: boolean;
}

const getCustomSelectStyles = (hasError?: boolean) => ({
  container: (provided: any) => ({
    ...provided,
    width: "100%",
  }),
  control: (provided: any, state: { selectProps: { menuIsOpen: boolean }; isDisabled?: boolean; isFocused?: boolean }) => ({
    ...provided,
    minHeight: "41px",
    height: "41px",
    borderRadius: state?.selectProps?.menuIsOpen ? "10px 10px 0px 0px" : "10px",
    border: hasError
      ? "1px solid #ef4444"
      : state?.isFocused && !state?.isDisabled
      ? "1px solid #0088cc"
      : "1px solid #ffffff",
    backgroundColor: state?.isDisabled ? "#f3f7fa" : "#ffffff",
    boxShadow: "none",
    cursor: state?.isDisabled ? "not-allowed" : "pointer",
    "&:hover": {
      border: hasError
        ? "1px solid #ef4444"
        : state?.isFocused && !state?.isDisabled
        ? "1px solid #0088cc"
        : "1px solid #ffffff",
    },
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    padding: "0 12px",
    height: "41px",
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontFamily: "inherit",
    fontSize: "12px",
    fontWeight: 500,
    color: "#B1B1B1",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontFamily: "inherit",
    fontSize: "12px",
    fontWeight: 500,
    color: "#111827",
  }),
  menu: (provided: any) => ({
    ...provided,
    marginTop: "0",
    borderRadius: "0 0 10px 10px",
    backgroundColor: "#ffffff",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    zIndex: 9999,
    maxHeight: "180px",
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: "4px 0",
    maxHeight: "180px",
    overflowY: "auto",
  }),
  option: (provided: any, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...provided,
    fontFamily: "inherit",
    fontSize: "12px",
    fontWeight: 400,
    color: state.isSelected ? "#ffffff" : "#111827",
    backgroundColor: state.isSelected
      ? "#0088cc"
      : state.isFocused
      ? "#f3f4f6"
      : "#ffffff",
    padding: "8px 12px",
    cursor: "pointer",
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    padding: "0 12px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
});

const CustomDropdownIndicator = (props: any) => {
  const { selectProps } = props;
  const isMenuOpen = selectProps.menuIsOpen;
  return (
    <components.DropdownIndicator {...props}>
      {isMenuOpen ? (
        <ExpandLess style={{ color: "#B1B1B1" }} fontSize="small" />
      ) : (
        <ExpandMore style={{ color: "#B1B1B1" }} fontSize="small" />
      )}
    </components.DropdownIndicator>
  );
};

const schema = yup.object().shape({
  dob: yup
    .string()
    .required("Date of birth is required")
    .test("no-future-date", "Date of birth cannot be in the future", (value) => {
      if (!value) return true;
      return dayjs(value).isBefore(dayjs().endOf("day"));
    }),
  gender: yup.string().required("Gender is required"),

  parentName: yup
    .string()
    .required("Parent Name is required")
    .trim()
    .max(100, "Parent name should not exceed 100 characters"),
  relationship: yup.string().required("Relationship is required"),
  parentPhone: yup
    .string()
    .required("Parent Phone Number is required")
    .test("is-valid-parentPhone", messages.validPhoneNumber, function (value) {
      const { path, createError } = this;
      if (!value) return true;
      const startsWithPlus = value.startsWith("+");
      if (!patterns.phoneNumber.test(value)) {
        return createError({ path, message: messages.validPhoneNumber });
      }
      if (startsWithPlus && value.length > 20) {
        return createError({ path, message: messages.phoneNumberLength });
      } else if (!startsWithPlus && value.length > 16) {
        return createError({ path, message: messages.phoneNumberLength16 });
      }
      return true;
    }),
  parentEmail: yup
    .string()
    .optional()
    .nullable()
    .trim()
    .test("is-valid-parentEmail", messages.validEmail, function (value) {
      const { path, createError } = this;
      if (value && value.length > 0) {
        return patterns.email.test(value) || createError({ path, message: messages.validEmail });
      }
      return true;
    }),

  currentPreviousSchoolName: yup
    .string()
    .required("School Name is required")
    .trim()
    .max(100, "School name should not exceed 100 characters"),
  academicLevel: yup.string().required("Academic Level is required"),

  communicationGroups: yup.array().of(yup.string()).optional(),

  location: yup.string().required("Location is required"),
  referralSource: yup.string().optional().nullable(),
  referralDetail: yup.string().optional().nullable(),

  registeredSimName: yup.string().optional().nullable(),
  networkProvider: yup.string().optional().nullable(),
  dataOptInFor: yup.string().optional().nullable(),
});

export const VirtualClassOnboardingModal: FunctionComponent<VirtualClassOnboardingModalProps> = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  isLoading = false,
}) => {
  const dispatch = useAppDispatch();
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);

  const { LogoutIcons, logoutGif } = images;
  const { success, error } = ToasterWithMessage();
  const { data: regOptionsData } = useQuery(GET_REGISTRATION_OPTIONS);
  const [saveStudentProfile, { loading: isSaving }] = useMutation(SAVE_STUDENT_PROFILE);

  const onLogoutClick = () => {
    setOpenConfirmationPopup(true);
  };

  const onClickYesButtonClick = () => {
    setOpenConfirmationPopup(false);
    dispatch(logout());
  };

  const onClickNoButtonClick = () => {
    setOpenConfirmationPopup(false);
  };

  const regOptions = regOptionsData?.registrationOptions;
  const genderOptions: string[] = regOptions?.genders || [];
  const relationshipOptions: string[] = regOptions?.guardianRelationships || [];
  const academicLevelOptions: string[] = regOptions?.academicLevels || [];
  const networkProviderOptions: string[] = regOptions?.networkProviders || [];
  const locationOptions: string[] = regOptions?.locations || [];
  const communicationGroupOptions: string[] = regOptions?.communicationGroups || [];
  const referralSourceOptions: string[] = regOptions?.referralSources || [];
  const dataOptInTargetsOptions: string[] = regOptions?.dataOptInTargets || [];

  const methods = useForm<IVirtualClassOnboardingInput>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      gender: "",
      dob: "",
      parentName: "",
      relationship: "",
      parentPhone: "",
      parentEmail: "",
      currentPreviousSchoolName: "",
      academicLevel: "",
      communicationGroups: [],
      location: "",
      referralSource: "",
      referralDetail: "",
      registeredSimName: "",
      networkProvider: "",
      dataOptInFor: "",
    },
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = methods;

  const communicationGroupsWatch = watch("communicationGroups") || [];
  const dataOptInForWatch = watch("dataOptInFor") || "";
  const referralSourceWatch = watch("referralSource") || "";

  const selectedSourceLower = referralSourceWatch.toLowerCase().trim();
  const isTeacherReferral = selectedSourceLower === "teacher referral";
  const isOther = selectedSourceLower === "other";
  const showReferralDetail = isTeacherReferral || isOther;

  if (!isOpen) return null;

  const handleFormSubmit = async (formData: IVirtualClassOnboardingInput) => {
    try {
      
      const formSourceLower = (formData.referralSource || "").toLowerCase().trim();
      const isTeacherRef = formSourceLower === "teacher referral";
      const isOth = formSourceLower === "other";
      const hasDetail = isTeacherRef || isOth;

      const response = await saveStudentProfile({
        variables: {
          input: {
            guardianName: formData.parentName,
            guardianPhone: formData.parentPhone,
            guardianEmail: formData.parentEmail || undefined,
            guardianRelationship: formData.relationship,
            details: {
              gender: formData.gender,
              dateOfBirth: formData.dob,
              currentLevel: formData.academicLevel,
              previousSchool: formData.currentPreviousSchoolName,
              location: formData.location,
              simName: formData.registeredSimName || undefined,
              networkProvider: formData.networkProvider || undefined,
              dataOptInFor: formData.dataOptInFor || undefined,
              communicationGroups: formData.communicationGroups || [],
              referralSource: formData.referralSource || undefined,
              referralDetail: hasDetail ? (formData.referralDetail || undefined) : undefined,
            },
          },
        },
      });

      const result = response?.data?.saveStudentProfile;
      if (result) {
        success("Profile saved successfully!");
        if (onSubmitSuccess) {
          onSubmitSuccess(formData);
        }
      }
    } catch (err: any) {
      error(err?.message || "Failed to save profile. Please try again.");
    }
  };

  return (
    <div className="vcmOverlay">
      <div className="vcmModalCard">
        {/* Header */}
        <div className="vcmHeader">
          <div>
            <h2 className="vcmTitle">Complete Your Student Profile</h2>
            <p className="vcmSubtitle">
              Please fill in your remaining information to finish setting up your account.
            </p>
          </div>
          <ButtonStyle
            onClick={onLogoutClick}
            bgcolor="var(--color-whitesmoke-300)"
            color="var(--color-dimgray)"
            height="32px"
            paddingLeft="14px"
            paddingRight="14px"
            minWidth="auto"
            deleteBtn={
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <img src={LogoutIcons} alt="logout" style={{ width: "14px", height: "14px", objectFit: "contain" }} />
                <span>Logout</span>
              </div>
            }
          />
        </div>

        {/* Body Form matching VirtualSchoolSignup structure */}
        <FormProvider {...methods}>
          <form className="vsRegCard vcmFormCard" onSubmit={handleSubmit(handleFormSubmit)} noValidate autoComplete="off">
            
            {/* Student Information */}
            <div className="vsRegSection">
              <h4 className="vsSectionTitle">
                Student Information <span className="vsReqStar">*</span>
              </h4>
              <div className="vsGrid2">
                <div className="vsField">
                  <Controller
                    control={control}
                    name="dob"
                    render={({ field }) => (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          disableFuture
                          maxDate={dayjs()}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(newValue) => {
                            field.onChange(newValue ? dayjs(newValue).format("YYYY-MM-DD") : "");
                          }}
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: {
                              onKeyDown: (e) => e.preventDefault(),
                              placeholder: "DOB",
                              error: !!errors.dob,
                              fullWidth: true,
                              sx: {
                                "& .MuiInputBase-root": {
                                  height: "41px",
                                  backgroundColor: "#ffffff",
                                  borderRadius: "10px",
                                  fontSize: "12px",
                                  fontFamily: "inherit",
                                  border: errors.dob ? "1px solid #ef4444" : "1px solid #ffffff",
                                  "& fieldset": { border: "none" },
                                },
                                "& .MuiInputBase-input": {
                                  padding: "12px",
                                  fontSize: "12px",
                                  color: "#111827",
                                  "&::placeholder": {
                                    color: "#B1B1B1",
                                    opacity: 1,
                                  },
                                },
                                "& .MuiSvgIcon-root": {
                                  color: "#B1B1B1",
                                  fontSize: "20px",
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    )}
                  />
                  {errors.dob && <span className="vsErrMsg">{errors.dob.message}</span>}
                </div>

                <div className="vsField">
                  <Controller
                    control={control}
                    name="gender"
                    render={({ field }) => (
                      <Select
                        id="gender-onboarding"
                        placeholder="Gender"
                        options={genderOptions.map((opt) => ({ value: opt, label: opt }))}
                        value={field.value ? { value: field.value, label: field.value } : null}
                        onChange={(selected: any) => field.onChange(selected?.value || "")}
                        styles={getCustomSelectStyles(!!errors.gender)}
                        components={{ DropdownIndicator: CustomDropdownIndicator }}
                        isSearchable={false}
                      />
                    )}
                  />
                  {errors.gender && <span className="vsErrMsg">{errors.gender.message}</span>}
                </div>
              </div>
            </div>

            {/* Parent / Guardian Details */}
            <div className="vsRegSection">
              <h4 className="vsSectionTitle">
                Parent / Guardian Details <span className="vsReqStar">*</span>
              </h4>
              <div className="vsGrid2">
                <div className="vsField">
                  <input
                    type="text"
                    placeholder="Parent Name"
                    {...register("parentName")}
                    className={`vsInput ${errors.parentName ? "inputError" : ""}`}
                  />
                  {errors.parentName && <span className="vsErrMsg">{errors.parentName.message}</span>}
                </div>
                <div className="vsField">
                  <Controller
                    control={control}
                    name="relationship"
                    render={({ field }) => (
                      <Select
                        id="relationship-onboarding"
                        placeholder="Relationship"
                        options={relationshipOptions.map((opt) => ({ value: opt, label: opt }))}
                        value={field.value ? { value: field.value, label: field.value } : null}
                        onChange={(selected: any) => field.onChange(selected?.value || "")}
                        styles={getCustomSelectStyles(!!errors.relationship)}
                        components={{ DropdownIndicator: CustomDropdownIndicator }}
                        isSearchable={false}
                      />
                    )}
                  />
                  {errors.relationship && <span className="vsErrMsg">{errors.relationship.message}</span>}
                </div>
              </div>

              <div className="vsGrid2">
                <div className="vsField">
                  <input
                    type="text"
                    placeholder="Parent Phone Number"
                    autoComplete="off"
                    {...register("parentPhone")}
                    className={`vsInput ${errors.parentPhone ? "inputError" : ""}`}
                  />
                  {errors.parentPhone && <span className="vsErrMsg">{errors.parentPhone.message}</span>}
                </div>
                <div className="vsField">
                  <input
                    type="email"
                    placeholder="Parent Email"
                    autoComplete="off"
                    {...register("parentEmail")}
                    className={`vsInput ${errors.parentEmail ? "inputError" : ""}`}
                  />
                  {errors.parentEmail && <span className="vsErrMsg">{errors.parentEmail.message}</span>}
                </div>
              </div>
            </div>

            {/* Academic Information */}
            <div className="vsRegSection">
              <h4 className="vsSectionTitle">
                Academic Information <span className="vsReqStar">*</span>
              </h4>
              <div className="vsGrid2">
                <div className="vsField">
                  <input
                    type="text"
                    placeholder="Current/Previous School Name"
                    {...register("currentPreviousSchoolName")}
                    className={`vsInput ${errors.currentPreviousSchoolName ? "inputError" : ""}`}
                  />
                  {errors.currentPreviousSchoolName && (
                    <span className="vsErrMsg">{errors.currentPreviousSchoolName.message}</span>
                  )}
                </div>
                <div className="vsField">
                  <Controller
                    control={control}
                    name="academicLevel"
                    render={({ field }) => (
                      <Select
                        id="academicLevel-onboarding"
                        placeholder="Academic Level"
                        options={academicLevelOptions.map((opt) => ({ value: opt, label: opt }))}
                        value={field.value ? { value: field.value, label: field.value } : null}
                        onChange={(selected: any) => field.onChange(selected?.value || "")}
                        styles={getCustomSelectStyles(!!errors.academicLevel)}
                        components={{ DropdownIndicator: CustomDropdownIndicator }}
                        isSearchable={false}
                      />
                    )}
                  />
                  {errors.academicLevel && <span className="vsErrMsg">{errors.academicLevel.message}</span>}
                </div>
              </div>
            </div>

            {/* WhatsApp Group Consent */}
            <div className="vsRegSection">
              <div className="vsCommunicationGroups">
                <label className="vsSubLabel">WhatsApp Group Consent</label>
                <div className="vsCheckboxList">
                  {communicationGroupOptions.map((opt) => (
                    <label key={opt} className="vsCheckboxOption">
                      <input
                        type="checkbox"
                        value={opt}
                        checked={communicationGroupsWatch.includes(opt)}
                        onChange={(e) => {
                          const current = getValues("communicationGroups") || [];
                          if (e.target.checked) {
                            setValue("communicationGroups", [...current, opt], { shouldValidate: true });
                          } else {
                            setValue("communicationGroups", current.filter((x) => x !== opt), { shouldValidate: true });
                          }
                        }}
                      />
                      <span className="vsCheckboxText">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="vsRegSection">
              <h4 className="vsSectionTitle">
                Additional Information <span className="vsReqStar">*</span>
              </h4>
              <div className="vsGrid2">
                <div className="vsField">
                  <Controller
                    control={control}
                    name="location"
                    render={({ field }) => (
                      <Select
                        id="location-onboarding"
                        placeholder="Select Your Location"
                        options={locationOptions.map((opt) => ({ value: opt, label: opt }))}
                        value={field.value ? { value: field.value, label: field.value } : null}
                        onChange={(selected: any) => field.onChange(selected?.value || "")}
                        styles={getCustomSelectStyles(!!errors.location)}
                        components={{ DropdownIndicator: CustomDropdownIndicator }}
                        isSearchable={false}
                      />
                    )}
                  />
                  {errors.location && <span className="vsErrMsg">{errors.location.message}</span>}
                </div>

                <div className="vsField">
                  <Controller
                    control={control}
                    name="referralSource"
                    render={({ field }) => (
                      <Select
                        id="referral-onboarding"
                        placeholder="Where you got to know about us"
                        options={referralSourceOptions.map((opt) => ({ value: opt, label: opt }))}
                        value={field.value ? { value: field.value, label: field.value } : null}
                        onChange={(selected: any) => {
                          const val = selected?.value || "";
                          if (val !== field.value) {
                            setValue("referralDetail", "");
                          }
                          field.onChange(val);
                        }}
                        styles={getCustomSelectStyles(!!errors.referralSource)}
                        components={{ DropdownIndicator: CustomDropdownIndicator }}
                        isSearchable={false}
                        isClearable
                      />
                    )}
                  />
                  {errors.referralSource && <span className="vsErrMsg">{errors.referralSource.message}</span>}
                </div>
              </div>

              {showReferralDetail && (
                <div className="vsGrid2">
                  <div className="vsField">
                    <input
                      type="text"
                      placeholder={isTeacherReferral ? "Please specify the teacher" : "Please specify"}
                      {...register("referralDetail")}
                      className={`vsInput ${errors.referralDetail ? "inputError" : ""}`}
                    />
                    {errors.referralDetail && <span className="vsErrMsg">{errors.referralDetail.message}</span>}
                  </div>
                </div>
              )}
            </div>

            {/* Data Information */}
            <div className="vsRegSection">
              <h4 className="vsSectionTitle">Data Information</h4>
              <div className="vsGrid2">
                <div className="vsField">
                  <input
                    type="text"
                    placeholder="Registered SIM Name"
                    {...register("registeredSimName")}
                    className={`vsInput ${errors.registeredSimName ? "inputError" : ""}`}
                  />
                  {errors.registeredSimName && <span className="vsErrMsg">{errors.registeredSimName.message}</span>}
                </div>
                <div className="vsField">
                  <Controller
                    control={control}
                    name="networkProvider"
                    render={({ field }) => (
                      <Select
                        id="networkProvider-onboarding"
                        placeholder="Network Provider"
                        options={networkProviderOptions.map((opt) => ({ value: opt, label: opt }))}
                        value={field.value ? { value: field.value, label: field.value } : null}
                        onChange={(selected: any) => field.onChange(selected?.value || "")}
                        styles={getCustomSelectStyles(!!errors.networkProvider)}
                        components={{ DropdownIndicator: CustomDropdownIndicator }}
                        isSearchable={false}
                        isClearable
                      />
                    )}
                  />
                  {errors.networkProvider && <span className="vsErrMsg">{errors.networkProvider.message}</span>}
                </div>
              </div>

              <div className="vsFreeDataConsent">
                <label className="vsSubLabel">Select Number for Free Data</label>
                <div className="vsCheckboxList">
                  {dataOptInTargetsOptions.map((opt) => (
                    <label key={opt} className="vsCheckboxOption">
                      <input
                        type="checkbox"
                        checked={dataOptInForWatch === opt}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setValue("dataOptInFor", opt, { shouldValidate: true });
                          } else {
                            setValue("dataOptInFor", "", { shouldValidate: true });
                          }
                        }}
                      />
                      <span className="vsCheckboxText">{opt}</span>
                    </label>
                  ))}
                </div>
                <div className="vsInfoNotice vsFreeDataNotice">
                  <InfoOutlined fontSize="small" className="vsInfoIcon" />
                  <span>For free data, payment must made before the program start date.</span>
                </div>
              </div>
            </div>

            {/* Modal Actions using ButtonStyle */}
            <div className="vsRegFooter">
              <ButtonStyle
                text={isSaving ? "Saving..." : "Save Details"}
                type="submit"
                disableButton={isSaving || isLoading}
              />
            </div>
          </form>
        </FormProvider>
      </div>
      <ConfirmationPopup
        open={openConfirmationPopup}
        message={LOGOUT_MESSAGE_NOT_IN_MEETING}
        gifData={logoutGif}
        yesButtonFunctionality={onClickYesButtonClick}
        noButtonFunctionality={onClickNoButtonClick}
        messageWidth={"345px"}
        popupContainerHeight={"202px"}
      />
    </div>
  );
};

export default VirtualClassOnboardingModal;
